import { GoToIntegration } from "@/schema/go-to-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const GO_TO_INTEGRATIONS_QUERY_KEY = ["go-to-integrations"]

export function useFetchGoToIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<GoToIntegration[]>({
    queryKey: [...GO_TO_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<GoToIntegration[]>(context, "go_to_integrations")
    },
    enabled,
  })
}

export function useGetGoToAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string } | void>({
    mutationKey: ["go-to-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) searchParams.append("return_url", params.returnUrl)
      const query = searchParams.toString()
      return apiContextFetch<AuthUriResponse>(context, `go_to_integrations/get_auth_uri${query ? `?${query}` : ""}`)
    },
  })
}

export function useDisconnectGoToIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["go-to-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      await apiContextFetch(context, `go_to_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GO_TO_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteGoToIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["go-to-integration-delete"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      await apiContextFetch(context, `go_to_integrations/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GO_TO_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
